/* RVP with QC › Merchant QC Setups (landing) — v5 mockup UI (rvpQc.css).
   Merchant list comes from the app's cached merchant-search API (no QC-summary list
   endpoint exists). Pick a merchant → editor, which loads/saves its real QC config (§3). */
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TextField, Alert, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PageTitle from '../../../components/PageTitle'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { CellLine, searchFieldSx, SearchAdornment, gridSx } from './gridCells'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../../redux/commonApiCalls'
import './rvpQc.css'

export default function RvpQcSetups() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const { data: merchants = [], isLoading, isError } = useGetAllActiveMerchantsIdAndNameCachedQuery()

  const rows = useMemo(() => {
    const list = merchants || []
    if (!q.trim()) return list
    const t = q.toLowerCase()
    return list.filter((m) => (m.name || '').toLowerCase().includes(t) || (m.companyName || '').toLowerCase().includes(t))
  }, [q, merchants])

  const columns = [
    { field: 'name', headerName: 'Merchant', flex: 1, minWidth: 200,
      renderCell: (params) => <CellLine bold>{params.row.name}</CellLine> },
    { field: 'companyName', headerName: 'Company', flex: 1, minWidth: 200,
      renderCell: (params) => <CellLine muted>{params.row.companyName || '—'}</CellLine> },
    { field: 'actions', headerName: 'Action', width: 140, sortable: false, align: 'right', headerAlign: 'right',
      renderCell: (params) => <button className="linkbtn" onClick={() => navigate(`/admin/rvp-qc/configure/${params.row.id}`)}>Edit</button> },
  ]

  return (
    <div className="rvpqc">
      <PageTitle>RVP with QC · Merchant QC Setups</PageTitle>

      <div className="card">
        {/* Header row — section title + count + primary action */}
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Merchants</h2>
          <Tooltip title="QC is configured per merchant and applies globally to all of that merchant’s reverse pickups (across every courier). Pick a merchant to configure or edit its QC.">
            <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
          <div className="spacer" />
          <span className="counter" style={{ marginRight: 12 }}><b>{rows.length}</b> merchants</span>
          <button className="btn" onClick={() => navigate('/admin/rvp-qc/configure/new')}>+ Create QC configuration</button>
        </div>
        {/* Toolbar row — search sits on its own line, not crammed against the header */}
        <div className="row" style={{ marginBottom: 14 }}>
          <TextField
            size="small" placeholder="Search merchant or company…" value={q} onChange={(e) => setQ(e.target.value)}
            sx={searchFieldSx} InputProps={SearchAdornment}
          />
        </div>
        {isLoading && <div className="ctx">Loading merchants…</div>}
        {isError && <Alert severity="warning" sx={{ mb: 2 }}>Failed to load merchants.</Alert>}
        {!isLoading && rows.length === 0 ? (
          <div className="empty"><h3>No merchants found</h3><p>Try a different search.</p></div>
        ) : (
          <ProzoDataGrid
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            rowCount={rows.length}
            pagination
            paginationMode="client"
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            flat={false}
            sx={gridSx}
          />
        )}
      </div>
    </div>
  )
}
